import React, { useState, useEffect } from "react";
import MerchantTableItem from "./merchantTableItem.jsx";
import {
  DangerButton,
  PrimaryButton,
  SecondaryButton,
} from "../../components/Buttons.jsx";
import ModalBasic from "../../components/ModalBasic.jsx";
import { useDispatch, useSelector } from "react-redux";
import config from '@/config';
import {
  archiveMerchant,
  archiveStore,
  createMerchantStripeAccount,
  findStripeCustomer,
  findStripeSubscriptionByCustomerID,
  getStripeCustomerById,
  getStripeSubscriptionById,
  updateOrg,
} from "../../api/admin.js";
import { addToast } from "../../store/app/action.js";
import Loader from "../../components/Loader.jsx";
import Tooltip from "../../components/Tooltip.jsx";
import ToggleSwitch from "../../components/ToggleSwitch.jsx";
import { FaRegEye } from "react-icons/fa6";
import SubscriptionModal from "./subscriptionModal.jsx";
import SubscriptionDot from "./subscriptionDot.jsx";

function MerchantTable({ merchants, getMerchants, subscriptions, emptyMessage = "No merchants to show." }) {
  const [showMerchantModal, setShowMerchantModal] = useState(false);
  const [showArchive, setShowArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const dispatch = useDispatch();
  if (!merchants) {
    return;
  }
  const handleClick = (id) => {
    setShowMerchantModal(id);
  };
  return (
    <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 relative">
      <div>
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="table-auto w-full dark:text-slate-300">
            {/* Table header */}
            <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Merchant ID</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Merchant Name</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">
                    (Active) Locations
                  </div>
                </th>
                {/*<th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">*/}
                {/*  <div className="font-semibold text-left">Enabled</div>*/}
                {/*</th>*/}
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Subscription</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  {/*<div className="font-semibold text-left">Mothership</div>*/}
                </th>
              </tr>
            </thead>
            {/* Table body */}
            <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
              {merchants.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-5 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                    {emptyMessage}
                  </td>
                </tr>
              ) : merchants.map((merchant) => {
                if (!merchant) {
                  return null;
                }
                return (
                  <MerchantTableItem
                    subscriptions={subscriptions}
                    merchant={merchant}
                    key={merchant.id}
                    handleClick={handleClick}
                    setShowArchive={setShowArchive}
                  />
                );
              })}
            </tbody>
          </table>
          <MerchantModal
            merchantID={showMerchantModal}
            setMerchantID={setShowMerchantModal}
            parentGetMerchants={getMerchants}
          />
          <ModalBasic
            width={"md"}
            modalOpen={Boolean(showArchive)}
            setModalOpen={setShowArchive}
            title={"Are you sure"}
          >
            <div className="text-sm space-y-4">
              <p>
                Are you sure you want to archive{" "}
                <span className="font-bold">{showArchive?.name}</span>?
              </p>
              <p>
                Doing so will also archive the following locations associated
                with this merchant.
              </p>
              <ul>
                {showArchive?.locations?.map((location) => {
                  return <li key={location.id}>{location.name}</li>;
                })}
              </ul>
              <p>
                Any pending charge will be settled and all their subscription
                license will end at the end of this billing cycle.
              </p>
            </div>
            <div className={"flex gap-2 justify-end pt-2"}>
              <SecondaryButton
                type="button"
                loading={archiving}
                onClick={(e) => {
                  e.preventDefault();
                  setShowArchive(null);
                }}
              >
                Cancel
              </SecondaryButton>
              <DangerButton
                onClick={async (e) => {
                  setArchiving(true);
                  try {
                    await archiveMerchant(showArchive.id);
                    getMerchants();
                    dispatch(
                      addToast({
                        type: "success",
                        message: `Successfully archived ${showArchive.name}`,
                        timer: 5,
                      })
                    );
                  } catch (e) {
                    dispatch(
                      addToast({
                        type: "error",
                        message: `Failed to archive ${showArchive.name}`,
                      })
                    );
                  } finally {
                    setArchiving(false);
                    setShowArchive(null);
                  }
                }}
                loading={archiving}
              >
                Archive
              </DangerButton>
            </div>
          </ModalBasic>
        </div>
      </div>
    </div>
  );
}

const FindStripeSubscriptionModal = ({
  setModalOpen,
  modalOpen,
  setMerchantSubscription,
  customerID,
}) => {
  const [loading, setLoading] = useState(false);
  const [subscriptionID, setSubscriptionID] = useState("");
  const [_subscriptions, _setSubscriptions] = useState([]);
  const dispatch = useDispatch();
  const handleFindSubscription = async () => {
    setLoading(true);
    findStripeSubscriptionByCustomerID(customerID).then((res) => {
      _setSubscriptions(res.subscriptions || []);
      setLoading(false);
    });
  };
  useEffect(() => {
    if (customerID) {
      handleFindSubscription();
    }
  }, [customerID]);
  return (
    <ModalBasic
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      title="Find Stripe Subscription"
      width="4xl"
    >
      <div>
        <div className={"flex gap-2 mt-2"}>
          <table className="table-auto w-full dark:text-slate-300 divide-y divide-slate-200 dark:divide-slate-700">
            <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-slate-200 dark:border-slate-700">
              <tr>
                <th className="text-left px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  Subscription ID
                </th>
                <th className="text-left px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  Description
                </th>
                <th className="text-left px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  Licenses
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {_subscriptions.map((subscription) => {
                return (
                  <tr key={subscription.id}>
                    <td className="text-left px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      {subscription.id}
                    </td>
                    <td className="text-left px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      {subscription.description}
                    </td>
                    <td className="text-left px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      {subscription.items?.data?.[0]?.quantity}
                    </td>
                    <td>
                      <SecondaryButton
                        onClick={() => {
                          setMerchantSubscription(subscription);
                          setModalOpen(false);
                        }}
                      >
                        Select
                      </SecondaryButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </ModalBasic>
  );
};
const FindStripeCustomerModal = ({
  setModalOpen,
  modalOpen,
  setMerchantCustomer,
  merchant,
}) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(merchant.billing_email || merchant.email);
  const [customers, setCustomers] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    setEmail(merchant.billing_email || merchant.email);
  }, [merchant]);
  const handleFindCustomer = async () => {
    setLoading(true);
    try {
      const res = await findStripeCustomer(email);
      setCustomers(res.customers || []);
    } catch (e) {
      let message = "please try again";
      try {
        const serverMessage = e.response?.data?.message;
        if (serverMessage) {
          message = serverMessage;
        }
      } catch {}

      dispatch(
        addToast({
          type: "error",
          message: "Error finding customer, " + message,
          timer: 5,
        })
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <ModalBasic
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      title="Find Stripe Account"
      width="4xl"
    >
      <div>
        <div className="flex flex-row mb-4 gap-2">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor={"email"}>
              Email
            </label>
            <div className="flex gap-1 items-center">
              <input
                className="form-input w-96"
                type={"text"}
                value={email}
                name={"email"}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-end">
            <div className={"flex gap-2 mt-2 justify-end items-end"}>
              <SecondaryButton onClick={() => setModalOpen(false)}>
                Cancel
              </SecondaryButton>
              <PrimaryButton loading={loading} onClick={handleFindCustomer}>
                Find
              </PrimaryButton>
            </div>
          </div>
        </div>

        <div className={"flex gap-2 mt-2"}>
          <table className="table-auto w-full dark:text-slate-300 divide-y divide-slate-200 dark:divide-slate-700">
            <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-slate-200 dark:border-slate-700">
              <tr>
                <th className="text-left px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  Customer ID
                </th>
                <th className="text-left px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  Email
                </th>
                <th className="text-left px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  Name
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => {
                return (
                  <tr key={customer.id}>
                    <td className="text-left px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      {customer.id}
                    </td>
                    <td className="text-left px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      {customer.email}
                    </td>
                    <td className="text-left px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      {customer.name}
                    </td>
                    <td>
                      <SecondaryButton
                        onClick={() => {
                          setMerchantCustomer(customer);
                          setModalOpen(false);
                        }}
                      >
                        Select
                      </SecondaryButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </ModalBasic>
  );
};
const MerchantModal = ({ merchantID, setMerchantID, parentGetMerchants }) => {
  const [merchant, setMerchant] = useState(null);
  const [merchantCustomer, setMerchantCustomer] = useState(null);
  const [merchantSubscription, setMerchantSubscription] = useState(null);
  const { merchants } = useSelector((state) => state.app);
  const [updatingOrg, setUpdatingOrg] = useState(false);
  const [findStripeCustomerModal, setFindStripeCustomerModal] = useState(false);
  const [viewSubscription, setViewSubscription] = useState(null);
  const [findStripeSubscriptionModal, setFindStripeSubscriptionModal] =
    useState(false);

  useEffect(() => {
    if (merchantID) {
      setMerchant(merchants.find((m) => m.id === merchantID));
    } else {
      setMerchant(null);
      setMerchantCustomer(null);
    }
  }, [merchantID]);

  useEffect(() => {
    if (merchant?.stripe_cust_id) {
      getStripeCustomerById(merchant.stripe_cust_id).then((res) => {
        setMerchantCustomer(res.customer);
      });
    } else {
      setMerchantCustomer(null);
    }
  }, [merchant?.stripe_cust_id]);
  useEffect(() => {
    if (merchant?.stripe_subscription_id) {
      getStripeSubscriptionById(merchant.stripe_subscription_id).then((res) => {
        setMerchantSubscription(res.subscription);
      });
    } else {
      setMerchantSubscription(null);
    }
  }, [merchant?.stripe_subscription_id]);

  const handleChange = (field, value) => {
    setMerchant({
      ...merchant,
      [field]: value,
    });
  };
  const dispatch = useDispatch();

  return (
    <ModalBasic
      modalOpen={!!merchantID}
      setModalOpen={() => {
        setMerchantID(null);
      }}
      title="Edit Merchant"
      width="xl"
    >
      {merchant && (
        <div>
          <div className="mb-4">
            <label
              className="block text-sm font-medium mb-1"
              htmlFor={"merchant_name"}
            >
              Name
            </label>
            <div className="flex gap-1 items-center">
              <input
                className="form-input w-96"
                type={"text"}
                value={merchant.name}
                name={"merchant_name"}
                onChange={(e) => handleChange("name", e.target.value)}
              />
            </div>
          </div>

          <div className="mb-4">
            {merchant.locations.length <= 1 ? (
              <>
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor={"direct_web_url"}
                >
                  Direct Web URL
                </label>
                Not available for single location merchants
              </>
            ) : (
              <>
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor={"direct_web_url"}
                >
                  Direct Web URL
                </label>
                <div className="flex gap-1 items-center">
                  <span className="flex">{config.direct_web_prefix}</span>
                  <input
                    className="form-input w-96"
                    type={"text"}
                    value={merchant.direct_web_url}
                    name={"direct_web_url"}
                    onChange={(e) =>
                      handleChange("direct_web_url", e.target.value)
                    }
                  />
                </div>
              </>
            )}
          </div>

          {merchant.mt_url && (
            <div className="mb-4">
              <>
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor={"mt_url"}
                >
                  Mautic URL
                </label>
                <div className="flex gap-1 items-center">
                  <input
                    className="form-input w-96"
                    type={"text"}
                    value={merchant.mt_url}
                    name={"mt_url"}
                    disabled
                  />
                </div>
              </>
            </div>
          )}

          {/* Payment Gateway, NMI or Stripe */}

          <div
            className={"border-b border-slate-200 dark:border-slate-700 mb-4"}
          ></div>

          <section className="mb-4">
            <label
              className="block text-lg font-medium mb-1"
              htmlFor={"enabled"}
            >
              Billing
            </label>

            {/* divider */}
            <div className="mb-4">
              <label
                className="block text-sm font-medium mb-1"
                htmlFor={"billing_email"}
              >
                Email
              </label>
              <div className="flex gap-1 items-center">
                <input
                  className="form-input w-96"
                  type={"text"}
                  value={merchant.billing_email}
                  name={"billing_email"}
                  onChange={(e) =>
                    handleChange("billing_email", e.target.value)
                  }
                />
              </div>
            </div>
          </section>
          <div className="mb-4">
            <>
              <label
                className="block text-sm font-medium mb-1"
                htmlFor={"direct_web_url"}
              >
                Merchant Stripe Customer Account
              </label>
              <div className="flex gap-2 items-center">
                {!merchant.stripe_cust_id && !merchantCustomer ? (
                  "Not linked"
                ) : merchantCustomer ? (
                  <>
                    {merchantCustomer.id}
                    <Tooltip>{merchantCustomer.email}</Tooltip>
                    <PrimaryButton
                      size="sm"
                      onClick={() => {
                        window.open(
                          `https://dashboard.stripe.com/${
                            config.environment === "development" ? "test/" : ""
                          }customers/${merchantCustomer.id}`,
                          "_blank"
                        );
                      }}
                    >
                      View on Stripe
                    </PrimaryButton>
                  </>
                ) : (
                  <Loader hideText size={8} />
                )}
                <SecondaryButton
                  loading={updatingOrg}
                  onClick={() => {
                    setFindStripeCustomerModal(true);
                  }}
                  size="sm"
                >
                  {merchant.stripe_cust_id || merchantCustomer
                    ? "Replace"
                    : "Link Existing"}
                </SecondaryButton>
                {!merchant.stripe_cust_id && (
                  <PrimaryButton
                    loading={updatingOrg}
                    onClick={async () => {
                      if (!merchant.billing_email) {
                        dispatch(
                          addToast({
                            type: "error",
                            message: "Please enter a billing email",
                            timer: 5,
                          })
                        );
                        return;
                      }
                      if (!merchant.name) {
                        dispatch(
                          addToast({
                            type: "error",
                            message: "Please enter a account name",
                            timer: 5,
                          })
                        );
                        return;
                      }
                      setUpdatingOrg(true);

                      try {
                        const newOrg = await createMerchantStripeAccount({
                          email: merchant.billing_email,
                          name: merchant.name,
                          merchant_id: merchant.id,
                        });
                        parentGetMerchants();
                        setMerchant({ ...merchant, ...newOrg.org });
                        setUpdatingOrg(false);
                        dispatch(
                          addToast({
                            type: "success",
                            message: "Stripe Account Created and Linked",
                            timer: 5,
                          })
                        );
                      } catch (e) {
                        let message = "please try again";
                        try {
                          const serverMessage = e.response?.data?.message;
                          if (serverMessage) {
                            message = serverMessage;
                          }
                        } catch {}

                        dispatch(
                          addToast({
                            type: "error",
                            message:
                              "Error creating stripe account, " + message,
                            timer: 5,
                          })
                        );
                        setUpdatingOrg(false);
                      }
                    }}
                  >
                    Create
                  </PrimaryButton>
                )}
              </div>
            </>
          </div>
          {/* merchant.arrgegated_billing */}
          <ToggleSwitch
            label="Manual Billing"
            checked={merchant.manual_billing}
            className="mb-4"
            onLabel="Manual Billing"
            offLabel="Manual Billing"
            onChange={(val) => handleChange("manual_billing", val)}
          />
          {!merchant.manual_billing && (
            <div className="mb-4 mt-4">
              <>
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor={"direct_web_url"}
                >
                  Subscription
                </label>
                <div className="flex gap-2 items-center">
                  {!merchant.stripe_subscription_id ? (
                    "No Stripe Subscription Linked"
                  ) : merchantSubscription ? (
                    <>
                      <SubscriptionDot subscription={merchantSubscription} />
                      {merchantSubscription.id}{" "}
                      <FaRegEye
                        onClick={() => {
                          setViewSubscription(merchantSubscription);
                        }}
                      />
                    </>
                  ) : (
                    <Loader hideText size={8} />
                  )}
                  <SecondaryButton
                    onClick={() => {
                      setFindStripeSubscriptionModal(true);
                    }}
                  >
                    {merchant.stripe_cust_id || merchantCustomer
                      ? "Replace"
                      : "Link"}
                  </SecondaryButton>
                </div>
              </>
            </div>
          )}

          {!merchant.manual_billing && (
            <div className="mb-4 mt-4">
              <>
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor={"direct_web_url"}
                >
                  Payment Method
                </label>
                <div className="flex gap-2 items-center">
                  {!merchant.stripe_card_id ? (
                    "No Payment Method"
                  ) : (
                    <>
                      {merchant.stripe_card_brand} {merchant.stripe_card_last4}{" "}
                      (exp: {merchant.stripe_card_exp_month}/
                      {merchant.stripe_card_exp_year})
                    </>
                  )}
                </div>
              </>
            </div>
          )}

          <FindStripeCustomerModal
            modalOpen={findStripeCustomerModal}
            setModalOpen={setFindStripeCustomerModal}
            setMerchantCustomer={(customer) => {
              setMerchantCustomer(customer);
              handleChange("stripe_cust_id", customer.id);
            }}
            merchant={merchant}
          />
          <FindStripeSubscriptionModal
            modalOpen={findStripeSubscriptionModal}
            setModalOpen={setFindStripeSubscriptionModal}
            setMerchantSubscription={(subscription) => {
              setMerchantSubscription(subscription);
              handleChange("stripe_subscription_id", subscription.id);
            }}
            customerID={merchant?.stripe_cust_id}
          />

          <div className={"flex gap-2 mt-2 justify-end"}>
            <SecondaryButton
              loading={updatingOrg}
              onClick={() => {
                setMerchantID(null);
              }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              loading={updatingOrg}
              onClick={async () => {
                if (merchant.payment_gateway === "nmi") {
                  if (!merchant.nmi_gateway_id) {
                    dispatch(
                      addToast({
                        type: "error",
                        message: "Please enter a gateway id",
                        timer: 5,
                      })
                    );
                    return;
                  }
                  if (!merchant.nmi_private_key) {
                    dispatch(
                      addToast({
                        type: "error",
                        message: "Please enter a private key",
                        timer: 5,
                      })
                    );
                    return;
                  }
                  if (!merchant.nmi_public_key) {
                    dispatch(
                      addToast({
                        type: "error",
                        message: "Please enter a public key",
                        timer: 5,
                      })
                    );
                    return;
                  }
                }else if (merchant.payment_gateway === "frame") {
                  if (!merchant.frame_public_key) {
                    dispatch(
                      addToast({
                        type: "error",
                        message: "Please enter a public key",
                        timer: 5,
                      })
                    );
                    return;
                  }
                  if (!merchant.frame_private_key) {
                    dispatch(
                      addToast({
                        type: "error",
                        message: "Please enter a private key",
                        timer: 5,
                      })
                    );
                    return;
                  }
                }

                setUpdatingOrg(true);
                try {
                  await updateOrg(merchant.id, merchant);
                  parentGetMerchants();
                  setMerchantID(null);
                } catch (e) {
                  let message = "please try again";
                  try {
                    const serverMessage = e.response?.data?.message;
                    if (serverMessage) {
                      message = serverMessage;
                    }
                  } catch {}

                  dispatch(
                    addToast({
                      type: "error",
                      message: "Error updating settings, " + message,
                      timer: 5,
                    })
                  );
                } finally {
                  setUpdatingOrg(false);
                }
              }}
            >
              Update
            </PrimaryButton>
          </div>
        </div>
      )}
      <SubscriptionModal
        subscription={viewSubscription}
        isOpen={!!viewSubscription}
        onClose={() => setViewSubscription(null)}
        merchant={merchant}
      />
    </ModalBasic>
  );
};

export { FindStripeCustomerModal, FindStripeSubscriptionModal };
export default MerchantTable;
